/**
 * Petavue tag — v1 Phase 5c (full v1 event set).
 *
 * Scope: server-assigned visitor cookie, sessions, client-side batching,
 * reliable transport, the *passive* v1 event set — `page_view`, `route_change`
 * (SPA), `click`, `scroll_depth`, `page_leave` — marketing attribution (UTM +
 * ad click-ids, referrer classification, first/last touch), the
 * engagement/session heartbeat engine (§1.7): a focused-only, session-persisted
 * `active_time` accumulator, `tab_blur`/`tab_focus`, a fixed-cadence
 * `engagement_ping` (every 5s), `session_start`/`session_end` — and
 * person capture (§1.4): the `identify()` host API, form-email + URL-email
 * signals, and `form_start`/`form_submit`/`form_abandon`.
 *
 * Discipline: everything is wrapped so the tag never throws into the host page;
 * raw form field values are never captured or transmitted (§1.4).
 */
(() => {
  const VISITOR_COOKIE = "_pv_id";
  // Sliding lifetime, re-stamped once per session for returning visitors (see
  // refreshVisitorId). A modest 180d window bounds the id even for an
  // always-active visitor.
  const VISITOR_TTL_SECONDS = 180 * 24 * 60 * 60; // 180d
  const SESSION_KEY = "_pv_session";
  const ATTR_KEY = "_pv_attr";
  const ENG_KEY = "_pv_eng";
  const IDENT_KEY = "_pv_ident";
  const SESSION_TIMEOUT_MS = 30 * 60 * 1000;

  // Person capture (§1.4). Conservative single-line email check; query keys
  // that may carry an email; trust order identify > form > url.
  const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const URL_EMAIL_KEYS = ["email", "email_address", "user_email"];
  const IDENTITY_TRUST: Record<string, number> = { identify: 3, form: 2, url: 1 };
  const FLUSH_MAX_EVENTS = 20;
  const FLUSH_INTERVAL_MS = 5000;

  // Engagement heartbeat (§1.7): fixed 5s cadence (no back-off) — start and cap
  // are equal and the step is 0, so the interval never grows.
  const PING_START_MS = 5000;
  const PING_STEP_MS = 0;
  const PING_MAX_MS = 5000;
  // Coalesce sub-second focus flip-flops (alt-tabbing) so they don't flood.
  const BLUR_DEBOUNCE_MS = 600;

  // Attribution params (§1.6.6).
  const UTM_KEYS = [
    "utm_source",
    "utm_medium",
    "utm_campaign",
    "utm_term",
    "utm_content",
    "utm_id",
  ] as const;
  const CLICK_ID_KEYS = [
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
    "li_fat_id",
    "ttclid",
    "twclid",
    "dclid",
    "epik",
  ] as const;
  // Click-ids that only appear on *paid* landings (used to classify referrer).
  const PAID_CLICK_KEYS = new Set([
    "gclid",
    "gbraid",
    "wbraid",
    "fbclid",
    "msclkid",
    "ttclid",
    "twclid",
    "dclid",
  ]);
  const SEARCH_HOSTS = [
    "google.",
    "bing.",
    "yahoo.",
    "duckduckgo.",
    "yandex.",
    "baidu.",
    "ecosia.",
    "brave.",
  ];
  const SOCIAL_HOSTS = [
    "facebook.",
    "instagram.",
    "twitter.",
    "t.co",
    "x.com",
    "linkedin.",
    "lnkd.in",
    "reddit.",
    "youtube.",
    "pinterest.",
    "tiktok.",
    "threads.",
  ];

  // --- config from the loader <script> tag ---------------------------------
  const script =
    (document.currentScript as HTMLScriptElement | null) ??
    document.querySelector("script[data-apikey]");
  const apiKey = script?.getAttribute("data-apikey") ?? "";
  const endpoint =
    script?.getAttribute("data-endpoint") ?? "http://localhost:3100";
  // Cookieless mode (backlog #7): the visitor id is derived server-side from a
  // fingerprint and cached in sessionStorage instead of a first-party cookie.
  const cookieless = script?.getAttribute("data-mode") === "cookieless";
  // Cross-domain cookie scoping (backlog #7). A customer spanning subdomains can
  // share one visitor cookie by naming their registrable domain explicitly via
  // `data-domain` (authoritative, zero-guess), or opt into a tiny suffix
  // heuristic with `data-cross-domain="true"`. Unset => host-only cookie (the
  // default, unchanged). No bundled Public Suffix List — see registrableDomain.
  const cookieDomain = script?.getAttribute("data-domain") ?? "";
  const crossDomain = script?.getAttribute("data-cross-domain") === "true";

  if (!apiKey) {
    // No tenant key => do nothing, silently.
    return;
  }

  // Privacy guard (cookieless only). #8 (consent/DNT/GPC) is the full layer;
  // until it lands, an opted-out visitor must not get a *stable* fingerprint.
  // When GPC/DNT is set we mint an ephemeral random id (below) that is never
  // fingerprinted server-side, so the visitor isn't trackable across sessions.
  const nav = navigator as Navigator & { globalPrivacyControl?: boolean };
  const privacyOptOut =
    cookieless &&
    (nav.globalPrivacyControl === true || nav.doNotTrack === "1");

  const collectUrl = `${endpoint}/v1/collect?k=${encodeURIComponent(apiKey)}`;

  // --- tiny helpers --------------------------------------------------------
  const uuid = (): string =>
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : // Fallback for old browsers.
        "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
          const r = (Math.random() * 16) | 0;
          const v = c === "x" ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });

  const now = (): number => Date.now();

  const currentUrl = (): string =>
    location.origin + location.pathname + location.search;

  const readCookie = (name: string): string | null => {
    const match = document.cookie.match(
      new RegExp("(?:^|; )" + name + "=([^;]*)"),
    );
    return match?.[1] ? decodeURIComponent(match[1]) : null;
  };

  // Resolve the cookie scope without a Public Suffix List (backlog #7). An
  // explicit `data-domain` wins verbatim (only the site owner authoritatively
  // knows their registrable domain). Otherwise, when `data-cross-domain` is set,
  // apply a tiny heuristic: registrable = last two labels, or last three when
  // the second-to-last is a known two-part suffix (e.g. `co.uk`, `com.au`).
  // Returns "" when it can't/shouldn't scope (single-label host, IP, or opt-out)
  // — the caller then writes a host-only cookie. Getting this wrong risks the
  // browser silently rejecting a Domain set on a public suffix, so we stay
  // conservative and lean on the explicit attribute.
  const TWO_PART_SLD = "co|com|org|net|gov|ac|edu";
  const TWO_PART_CC = "uk|au|nz|in|br|jp|za";
  const registrableDomain = (host: string): string => {
    if (cookieDomain) return cookieDomain;
    if (!crossDomain) return "";
    // Never scope an IP literal or a single-label host.
    if (/^[\d.]+$/.test(host) || /^\[.*\]$/.test(host)) return "";
    const labels = host.split(".");
    if (labels.length < 2) return "";
    const sld = labels[labels.length - 2] ?? "";
    const tld = labels[labels.length - 1] ?? "";
    const twoPart =
      labels.length >= 3 &&
      new RegExp(`^(${TWO_PART_SLD})$`).test(sld) &&
      new RegExp(`^(${TWO_PART_CC})$`).test(tld);
    return labels.slice(twoPart ? -3 : -2).join(".");
  };

  const writeVisitorCookie = (id: string): void => {
    const host = location.hostname;
    const d = registrableDomain(host);
    // Only add Domain= when it actually broadens scope beyond the current host.
    const domainAttr = d && d !== host ? `; Domain=${d}` : "";
    // Secure only over https so http-localhost dev still sets the cookie.
    const secure = location.protocol === "https:" ? "; Secure" : "";
    document.cookie = `${VISITOR_COOKIE}=${encodeURIComponent(
      id,
    )}; Path=/; Max-Age=${VISITOR_TTL_SECONDS}; SameSite=Lax${domainAttr}${secure}`;
  };

  // Visitor-id store abstraction (backlog #7). Cookie mode persists the
  // server-minted id in a first-party cookie; cookieless mode caches the
  // fingerprint-derived id in sessionStorage (no persistent client storage —
  // cross-session stability comes from the server re-deriving the same id).
  const readVisitorId = (): string | null => {
    if (!cookieless) return readCookie(VISITOR_COOKIE);
    try {
      return sessionStorage.getItem(VISITOR_COOKIE);
    } catch {
      return null;
    }
  };

  const writeVisitorId = (id: string): void => {
    if (!cookieless) {
      writeVisitorCookie(id);
      return;
    }
    try {
      sessionStorage.setItem(VISITOR_COOKIE, id);
    } catch {
      /* ignore */
    }
  };

  // Sliding-expiry refresh: re-stamp the existing visitor cookie so an active
  // visitor's window slides forward while dormant ids still expire. Called once
  // per session (not per event). No-op in cookieless mode (no persistent cookie)
  // and for privacy opt-out (GPC/DNT) sessions. On first contact readVisitorId()
  // is null, so this is a natural no-op until the id has been minted. This is a
  // plain first-party cookie refresh — Safari/Brave (ITP) still cap JS-set
  // cookies to ~7d, and that cap is treated as intended, not something to defeat.
  const refreshVisitorId = (): void => {
    if (cookieless || privacyOptOut) return;
    const id = readVisitorId();
    if (id) writeVisitorCookie(id);
  };

  // --- session -------------------------------------------------------------
  interface SessionState {
    id: string;
    last_active: number;
    started_at: number;
  }

  // Set true whenever getSession() mints a fresh id, so the init path can emit
  // exactly one `session_start` per session.
  let sessionIsNew = false;

  const getSession = (): SessionState => {
    let state: SessionState | null = null;
    try {
      const raw = sessionStorage.getItem(SESSION_KEY);
      if (raw) state = JSON.parse(raw) as SessionState;
    } catch {
      /* ignore */
    }
    const t = now();
    if (!state || t - state.last_active > SESSION_TIMEOUT_MS) {
      state = { id: uuid(), last_active: t, started_at: t };
      sessionIsNew = true;
    } else {
      state.last_active = t;
      // Back-compat: sessions minted before started_at existed.
      if (typeof state.started_at !== "number") state.started_at = t;
    }
    try {
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(state));
    } catch {
      /* ignore */
    }
    return state;
  };

  // --- attribution (§1.6.6) ------------------------------------------------
  type ReferrerType = "direct" | "referral" | "search" | "social" | "paid";

  interface Attribution {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
    utm_id?: string;
    first_click_id?: string;
    click_ids?: Record<string, string>;
    referrer_type?: ReferrerType;
    landing_page?: string;
    entry_referrer?: string;
    last?: Partial<Attribution> | null;
  }

  const classifyReferrer = (
    referrer: string,
    clickIds: Record<string, string>,
  ): ReferrerType => {
    if (Object.keys(clickIds).some((k) => PAID_CLICK_KEYS.has(k))) return "paid";
    if (!referrer) return "direct";
    let host = "";
    try {
      host = new URL(referrer).hostname.toLowerCase();
    } catch {
      return "direct";
    }
    if (host === location.hostname.toLowerCase()) return "direct"; // internal
    if (SEARCH_HOSTS.some((s) => host.includes(s))) return "search";
    if (SOCIAL_HOSTS.some((s) => host.includes(s))) return "social";
    return "referral";
  };

  /** Parse UTM + click-ids from the current URL into an attribution touch. */
  const parseTouch = () => {
    const params = new URLSearchParams(location.search);
    const utm: Record<string, string> = {};
    for (const k of UTM_KEYS) {
      const v = params.get(k);
      if (v) utm[k] = v;
    }
    const clickIds: Record<string, string> = {};
    let firstClickId: string | undefined;
    for (const k of CLICK_ID_KEYS) {
      const v = params.get(k);
      if (v) {
        clickIds[k] = v;
        if (!firstClickId) firstClickId = v;
      }
    }
    const hasParams =
      Object.keys(utm).length > 0 || Object.keys(clickIds).length > 0;
    return {
      utm,
      clickIds,
      firstClickId,
      hasParams,
      referrer_type: classifyReferrer(document.referrer, clickIds),
    };
  };

  interface StoredAttr {
    sid: string;
    attr: Attribution;
  }

  /**
   * First-touch is captured once per session (persisted in `sessionStorage`);
   * a later landing carrying fresh params sets last-touch. Kept separate from
   * the raw query string (§1.6.6).
   */
  const readAttribution = (): Attribution | undefined => {
    try {
      const sid = getSession().id;
      let stored: StoredAttr | null = null;
      try {
        const raw = sessionStorage.getItem(ATTR_KEY);
        if (raw) stored = JSON.parse(raw) as StoredAttr;
      } catch {
        /* ignore */
      }
      const t = parseTouch();

      // New (or first) session → capture first-touch.
      if (!stored || stored.sid !== sid) {
        const attr: Attribution = {
          ...t.utm,
          ...(t.firstClickId ? { first_click_id: t.firstClickId } : {}),
          ...(Object.keys(t.clickIds).length ? { click_ids: t.clickIds } : {}),
          referrer_type: t.referrer_type,
          landing_page: currentUrl(),
          ...(document.referrer ? { entry_referrer: document.referrer } : {}),
          last: null,
        };
        try {
          sessionStorage.setItem(ATTR_KEY, JSON.stringify({ sid, attr }));
        } catch {
          /* ignore */
        }
        return attr;
      }

      // Existing session → update last-touch only if this landing has params.
      if (t.hasParams) {
        stored.attr.last = {
          ...t.utm,
          ...(t.firstClickId ? { first_click_id: t.firstClickId } : {}),
          referrer_type: t.referrer_type,
          landing_page: currentUrl(),
        };
        try {
          sessionStorage.setItem(ATTR_KEY, JSON.stringify(stored));
        } catch {
          /* ignore */
        }
      }
      return stored.attr;
    } catch {
      return undefined;
    }
  };

  // --- person identity capture (§1.4) --------------------------------------
  // One current identity per session (highest-trust wins), persisted like
  // attribution. The extracted email rides in `user.identity`; raw field
  // values are never stored or sent.
  type IdentMethod = "identify" | "form" | "url";
  interface Identity {
    email: string;
    method: IdentMethod;
    traits?: Record<string, unknown>;
  }
  interface StoredIdent {
    sid: string;
    identity: Identity;
  }

  let currentIdentity: Identity | null = null;

  const loadIdentity = (): void => {
    try {
      const sid = getSession().id;
      const raw = sessionStorage.getItem(IDENT_KEY);
      const stored = raw ? (JSON.parse(raw) as StoredIdent) : null;
      if (stored && stored.sid === sid) currentIdentity = stored.identity;
    } catch {
      /* ignore */
    }
  };

  const captureIdentity = (
    email: string,
    method: IdentMethod,
    traits?: Record<string, unknown>,
  ): void => {
    try {
      const norm = (email || "").trim().toLowerCase();
      if (!EMAIL_RE.test(norm)) return;
      // A lower-trust signal must not clobber a higher-trust one.
      if (
        currentIdentity &&
        (IDENTITY_TRUST[method] ?? 0) <
          (IDENTITY_TRUST[currentIdentity.method] ?? 0)
      )
        return;
      currentIdentity = { email: norm, method, ...(traits ? { traits } : {}) };
      try {
        sessionStorage.setItem(
          IDENT_KEY,
          JSON.stringify({ sid: getSession().id, identity: currentIdentity }),
        );
      } catch {
        /* ignore */
      }
      // Send promptly so each distinct signal lands in its own batch.
      flush(false);
    } catch {
      /* ignore */
    }
  };

  const identify = (email: unknown, traits?: unknown): void => {
    if (typeof email !== "string") return;
    captureIdentity(
      email,
      "identify",
      traits && typeof traits === "object"
        ? (traits as Record<string, unknown>)
        : undefined,
    );
  };

  /** URL-email person source: an `email`-like query key that validates. */
  const captureUrlEmail = (): void => {
    try {
      const params = new URLSearchParams(location.search);
      for (const k of URL_EMAIL_KEYS) {
        const v = params.get(k);
        if (v && EMAIL_RE.test(v.trim().toLowerCase())) {
          captureIdentity(v, "url");
          return;
        }
      }
    } catch {
      /* ignore */
    }
  };

  // Host API: an async command queue so calls made before this (async) tag
  // loads are preserved. Stub: window.petavue = window.petavue || function(){
  //   (petavue.q = petavue.q || []).push(arguments) }
  const dispatch = (args: ArrayLike<unknown>): void => {
    try {
      const arr = Array.prototype.slice.call(args);
      if (arr[0] === "identify") identify(arr[1], arr[2]);
      // Unknown commands are ignored (forward-compatible).
    } catch {
      /* ignore */
    }
  };

  const installHostApi = (): void => {
    try {
      const w = window as unknown as {
        petavue?: (((...a: unknown[]) => void) & { q?: unknown[] }) | undefined;
      };
      const queued = (w.petavue && w.petavue.q) || [];
      const api = function (...a: unknown[]): void {
        dispatch(a);
      } as ((...a: unknown[]) => void) & { q?: unknown[] };
      w.petavue = api;
      for (const item of queued) dispatch(item as ArrayLike<unknown>);
    } catch {
      /* ignore */
    }
  };

  // --- per-request context (§1.6.5, abbreviated) ---------------------------
  const context = () => ({
    ua: navigator.userAgent,
    lang: navigator.language,
    tz: -new Date().getTimezoneOffset(),
    webdriver: navigator.webdriver === true,
    screen: `${screen.width}x${screen.height}x${screen.colorDepth}`,
    viewport: `${innerWidth}x${innerHeight}`,
    cores: navigator.hardwareConcurrency,
    platform: navigator.platform,
  });

  // --- event buffer + transport --------------------------------------------
  interface QueuedEvent {
    event_id: string;
    event_type: string;
    ts_client: number;
    page_visit_id: string;
    [k: string]: unknown;
  }

  let buffer: QueuedEvent[] = [];
  let flushTimer: number | null = null;
  let sending = false;
  let retryCount = 0;
  let retryTimer: number | null = null;

  // Delivery durability (§1.7 / backlog #5). A batch is only dropped from the
  // buffer once the collector gives a definitive answer: 2xx = delivered;
  // 400/401/403 = terminal (retrying can't help) so drop; 429 (rate limit, see
  // backlog #1) / 5xx / network error = transient, so re-queue and retry with
  // capped exponential backoff. sendBeacon can't confirm delivery, so the
  // observed-retry path always uses fetch; the unload beacon stays best-effort.
  const MAX_BUFFER = 100; // replay bound: retained events cap (oldest shed first)
  const MAX_RETRIES = 5; // consecutive failures before parking the backlog
  const RETRY_BASE_MS = 2000;
  const RETRY_MAX_MS = 30000;
  const STORAGE_KEY = "_pv_q"; // L3 backlog: survives page close + reload (#5)
  const MAX_STORE_AGE_MS = 24 * 60 * 60 * 1000; // drop persisted events >24h old

  // status 0 == network error / no response. 429 + any 5xx are transient.
  const retryable = (status: number): boolean =>
    status === 0 || status === 429 || status >= 500;

  // Re-queue a failed batch at the FRONT (rough order preserved), bounded so a
  // long outage can't grow memory without limit — oldest events are shed first.
  const requeue = (events: QueuedEvent[]): void => {
    buffer = events.concat(buffer);
    if (buffer.length > MAX_BUFFER)
      buffer = buffer.slice(buffer.length - MAX_BUFFER);
    persistBuffer();
  };

  // L3 durability (backlog #5): mirror a bounded slice of the buffer to
  // localStorage so a backlog survives page close + reload — the only way to
  // recover events for a tab closed while offline (in-memory re-queue can't).
  // Idempotent replay is safe: the collector dedupes on event_id (Redis SETNX).
  // Fully guarded — private-mode / quota / disabled storage all silently no-op.
  const persistBuffer = (): void => {
    if (privacyOptOut) return; // opted-out sessions leave no cross-load state
    try {
      if (buffer.length === 0) {
        localStorage.removeItem(STORAGE_KEY);
        return;
      }
      const v = buffer.slice(-MAX_BUFFER);
      localStorage.setItem(STORAGE_KEY, JSON.stringify({ v, t: now() }));
    } catch {
      /* ignore */
    }
  };

  // Load + clear any persisted backlog; drop the record wholesale if it's older
  // than MAX_STORE_AGE_MS or malformed. Opted-out sessions never read state.
  const loadPersisted = (): QueuedEvent[] => {
    if (privacyOptOut) return [];
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      localStorage.removeItem(STORAGE_KEY);
      const rec = JSON.parse(raw) as { v?: QueuedEvent[]; t?: number };
      if (!rec || !Array.isArray(rec.v)) return [];
      if (typeof rec.t !== "number" || now() - rec.t > MAX_STORE_AGE_MS)
        return [];
      return rec.v;
    } catch {
      return [];
    }
  };

  const scheduleRetry = (events: QueuedEvent[]): void => {
    requeue(events);
    if (retryCount >= MAX_RETRIES) {
      // Give up actively retrying; the backlog stays buffered (bounded) and
      // rides out on the next organic enqueue/flush.
      retryCount = 0;
      return;
    }
    retryCount++;
    if (retryTimer !== null) return;
    const delay = Math.min(
      RETRY_MAX_MS,
      RETRY_BASE_MS * Math.pow(2, retryCount - 1),
    );
    retryTimer = window.setTimeout(() => {
      retryTimer = null;
      flush(false);
    }, delay);
  };

  const buildBody = (events: QueuedEvent[]) =>
    JSON.stringify({
      customer: {
        website: location.hostname,
        mode: cookieless ? "cookieless" : "cookie",
      },
      user: {
        visitor_id: readVisitorId(),
        session_id: getSession().id,
        nonce: uuid(),
        attribution: readAttribution(),
        ...(currentIdentity
          ? {
              identity: currentIdentity.email,
              identity_method: currentIdentity.method,
              ...(currentIdentity.traits
                ? { traits: currentIdentity.traits }
                : {}),
            }
          : {}),
        context: context(),
      },
      events,
    });

  const flush = (viaBeacon: boolean): void => {
    if (sending || buffer.length === 0) return;
    const events = buffer;
    buffer = [];

    const hasVisitor = readVisitorId() !== null;
    const body = buildBody(events);

    // Unload path: sendBeacon survives the tab going away but can't confirm
    // delivery, so it's fire-and-forget (no retry). Only valid once we hold a
    // visitor cookie — first contact must read the minted id via fetch.
    if (viaBeacon && hasVisitor) {
      const blob = new Blob([body], { type: "application/json" });
      if (navigator.sendBeacon(collectUrl, blob)) return;
      // Beacon refused (queue full / payload too large): re-queue for a later
      // flush rather than dropping. The tab may still be alive (blur, not unload).
      requeue(events);
      return;
    }

    // Observed send: fetch lets us read the outcome and retry transient ones.
    sending = true;
    fetch(collectUrl, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body,
      keepalive: true,
    })
      .then((res) => {
        if (res.ok) {
          retryCount = 0; // reached the server with a success
          // 200 (first contact) carries the minted visitor id; 204 is empty.
          if (!hasVisitor)
            return res.json().then(
              (data: { visitor_id?: string } | null) => {
                if (data?.visitor_id) writeVisitorId(data.visitor_id);
              },
              () => {
                /* empty / non-JSON body */
              },
            );
          return;
        }
        // Non-2xx: retry transient outcomes (429/5xx), drop terminal 4xx
        // (bad key/origin/shape — retrying can't help).
        if (retryable(res.status)) scheduleRetry(events);
      })
      .catch(() => {
        // Network error / offline: transient — re-queue + back off.
        scheduleRetry(events);
      })
      .finally(() => {
        sending = false;
        // Reconcile the L3 store with the post-send buffer (backlog #5): empty
        // after a delivered batch, or the re-queued backlog after a transient
        // failure — so a reload never replays already-delivered events.
        persistBuffer();
        // If a retry fired while this send was in flight, its events are parked
        // in the buffer; make sure they get drained without a new organic event.
        if (buffer.length > 0 && retryTimer === null) scheduleFlush();
      });
  };

  const scheduleFlush = (): void => {
    if (flushTimer !== null) return;
    flushTimer = window.setTimeout(() => {
      flushTimer = null;
      flush(false);
    }, FLUSH_INTERVAL_MS);
  };

  const enqueue = (
    type: string,
    pageVisitId: string,
    fields: Record<string, unknown>,
  ): void => {
    buffer.push({
      event_id: uuid(),
      event_type: type,
      ts_client: now(),
      page_visit_id: pageVisitId,
      ...fields,
    });
    if (buffer.length >= FLUSH_MAX_EVENTS) flush(false);
    else scheduleFlush();
    // Persist the backlog so events survive a page close before the next flush.
    persistBuffer();
  };

  // --- page-visit lifecycle ------------------------------------------------
  interface PageVisit {
    id: string;
    url: string;
    startedAt: number;
    maxScrollPct: number;
    milestones: Set<number>;
  }

  let pageVisit!: PageVisit;
  let lastHref = location.href;

  const startPageVisit = (): void => {
    pageVisit = {
      id: uuid(),
      url: currentUrl(),
      startedAt: now(),
      maxScrollPct: 0,
      milestones: new Set<number>(),
    };
    // Dwell is measured per page; restart the heartbeat back-off (§1.7).
    resetPing();
  };

  const trackPageView = (loadType?: string): void => {
    bumpPageCount();
    const nav = performance.getEntriesByType(
      "navigation",
    )[0] as PerformanceNavigationTiming | undefined;
    enqueue("page_view", pageVisit.id, {
      url: pageVisit.url,
      title: document.title,
      referrer: document.referrer,
      load_type: loadType ?? nav?.type ?? "initial",
    });
  };

  const emitPageLeave = (): void => {
    if (!pageVisit) return;
    checkpoint();
    enqueue("page_leave", pageVisit.id, {
      url: pageVisit.url,
      time_on_page: now() - pageVisit.startedAt,
      max_scroll: pageVisit.maxScrollPct,
      active_time: currentActiveTime(),
    });
  };

  // --- capture: SPA route changes ------------------------------------------
  const handleRouteChange = (): void => {
    const href = location.href;
    if (href === lastHref) return; // no-op replaceState / same url
    lastHref = href;
    const fromUrl = pageVisit ? pageVisit.url : undefined;
    emitPageLeave();
    emitFormAbandons();
    startPageVisit();
    trackPageView("spa");
    enqueue("route_change", pageVisit.id, {
      from_url: fromUrl,
      to_url: pageVisit.url,
    });
  };

  const patchHistory = (name: "pushState" | "replaceState"): void => {
    const orig = history[name];
    history[name] = function (
      this: History,
      ...args: Parameters<History["pushState"]>
    ) {
      const ret = orig.apply(this, args);
      try {
        handleRouteChange();
      } catch {
        /* fail silent */
      }
      return ret;
    } as History["pushState"];
  };

  // --- capture: clicks (delegated, capture phase) --------------------------
  const clickText = (el: Element): string | undefined =>
    (el.textContent || "").trim().slice(0, 100) || undefined;

  const onClick = (e: Event): void => {
    try {
      const target = e.target;
      if (!(target instanceof Element)) return;
      const el =
        target.closest(
          "a,button,[role=button],input[type=submit],input[type=button]",
        ) ?? target;
      enqueue("click", pageVisit.id, {
        tag: el.tagName ? el.tagName.toLowerCase() : undefined,
        id: el.id || undefined,
        classes:
          typeof el.className === "string" && el.className
            ? el.className
            : undefined,
        text: clickText(el),
        href: el instanceof HTMLAnchorElement ? el.href : undefined,
      });
    } catch {
      /* fail silent */
    }
  };

  // --- capture: scroll depth (throttled, milestones) -----------------------
  let scrollScheduled = false;
  const measureScroll = (): void => {
    scrollScheduled = false;
    if (!pageVisit) return;
    const scrollable = document.documentElement.scrollHeight - innerHeight;
    const pct =
      scrollable > 0
        ? Math.min(100, Math.round((scrollY / scrollable) * 100))
        : 100;
    if (pct > pageVisit.maxScrollPct) pageVisit.maxScrollPct = pct;
    for (const m of [25, 50, 75, 100]) {
      if (pct >= m && !pageVisit.milestones.has(m)) {
        pageVisit.milestones.add(m);
        enqueue("scroll_depth", pageVisit.id, { depth_pct: m });
      }
    }
  };
  const onScroll = (): void => {
    if (scrollScheduled) return;
    scrollScheduled = true;
    requestAnimationFrame(measureScroll);
  };

  // --- capture: forms (§1.3 form lifecycle / §1.4 form-email) ---------------
  // Delegated + capture-phase, mirroring onClick. We emit a value-free form
  // *descriptor* only; the single extracted email becomes a `form` person
  // signal via captureIdentity — raw field values never leave the page.
  const startedForms = new WeakSet<HTMLFormElement>();
  const submittedForms = new WeakSet<HTMLFormElement>();
  const openForms = new Set<HTMLFormElement>(); // started, not yet submitted

  const formDescriptor = (form: HTMLFormElement): Record<string, unknown> => {
    const name = form.getAttribute("name") || undefined;
    return {
      form_id: form.id || undefined,
      name,
      action: form.getAttribute("action") || undefined,
      selector: form.id
        ? "form#" + form.id
        : name
          ? 'form[name="' + name + '"]'
          : "form",
      field_count: form.elements ? form.elements.length : undefined,
    };
  };

  /** First value passing the email check; skips password/hidden/submit/button
   * and prefers explicit email inputs. Never stores the value. */
  const extractFormEmail = (form: HTMLFormElement): void => {
    try {
      const els = form.elements;
      for (const emailOnly of [true, false]) {
        for (let i = 0; i < els.length; i++) {
          const el = els[i] as HTMLInputElement;
          const type = (el.type || "").toLowerCase();
          if (
            type === "password" ||
            type === "hidden" ||
            type === "submit" ||
            type === "button"
          )
            continue;
          if (emailOnly && type !== "email") continue;
          const val = typeof el.value === "string" ? el.value.trim() : "";
          if (val && EMAIL_RE.test(val.toLowerCase())) {
            captureIdentity(val, "form");
            return;
          }
        }
      }
    } catch {
      /* ignore */
    }
  };

  const onFormStart = (e: Event): void => {
    try {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const form = t.closest("form");
      if (!(form instanceof HTMLFormElement) || startedForms.has(form)) return;
      startedForms.add(form);
      openForms.add(form);
      enqueue("form_start", pageVisit.id, formDescriptor(form));
    } catch {
      /* ignore */
    }
  };

  const markSubmit = (form: HTMLFormElement): void => {
    if (submittedForms.has(form)) return; // dedup native submit + heuristics
    submittedForms.add(form);
    openForms.delete(form);
    enqueue("form_submit", pageVisit.id, formDescriptor(form));
    extractFormEmail(form);
  };

  const onSubmit = (e: Event): void => {
    if (e.target instanceof HTMLFormElement) markSubmit(e.target);
  };

  // Heuristics for JS forms that never fire a native submit.
  const onSubmitClick = (e: Event): void => {
    try {
      const t = e.target;
      if (!(t instanceof Element)) return;
      const btn = t.closest("button, input[type=submit]");
      if (!btn) return;
      if (btn instanceof HTMLButtonElement && btn.type === "button") return;
      const form = btn.closest("form");
      if (form instanceof HTMLFormElement) markSubmit(form);
    } catch {
      /* ignore */
    }
  };

  const onSubmitKey = (e: KeyboardEvent): void => {
    try {
      if (e.key !== "Enter") return;
      const t = e.target;
      if (!(t instanceof Element) || t.tagName !== "INPUT") return;
      const form = t.closest("form");
      if (form instanceof HTMLFormElement) markSubmit(form);
    } catch {
      /* ignore */
    }
  };

  const emitFormAbandons = (): void => {
    try {
      for (const form of openForms)
        enqueue("form_abandon", pageVisit.id, formDescriptor(form));
      openForms.clear();
    } catch {
      /* ignore */
    }
  };

  // --- engagement engine (§1.7): active_time + heartbeat + focus events -----
  // `active_time` counts only while the tab is focused (idle excluded) and is
  // session-scoped + persisted in `sessionStorage`, so it survives hard
  // navigations within a session (MPA). `focusedSince` holds the start of the
  // current focused span; `activeMs` is the folded total. The heartbeat pings
  // are safety checkpoints (each carries the running total, not a delta) so
  // dwell survives a lost unload beacon.
  interface EngState {
    sid: string;
    active_ms: number;
    page_count: number;
  }

  let engSid = "";
  let activeMs = 0;
  let pageCount = 0;
  let focusedSince: number | null = null;
  let blurAt = 0;

  const loadEng = (): void => {
    engSid = getSession().id;
    try {
      const raw = sessionStorage.getItem(ENG_KEY);
      const stored = raw ? (JSON.parse(raw) as EngState) : null;
      if (stored && stored.sid === engSid) {
        activeMs = stored.active_ms || 0;
        pageCount = stored.page_count || 0;
      }
    } catch {
      /* ignore */
    }
  };

  const saveEng = (): void => {
    try {
      sessionStorage.setItem(
        ENG_KEY,
        JSON.stringify({ sid: engSid, active_ms: activeMs, page_count: pageCount }),
      );
    } catch {
      /* ignore */
    }
  };

  const currentActiveTime = (): number =>
    activeMs + (focusedSince !== null ? now() - focusedSince : 0);

  /** Fold the live focused span into `activeMs` and persist a near-complete
   * snapshot (so the next hard load restores it). Safe to call repeatedly. */
  const checkpoint = (): void => {
    if (focusedSince !== null) {
      const t = now();
      activeMs += t - focusedSince;
      focusedSince = t;
    }
    saveEng();
  };

  const bumpPageCount = (): void => {
    pageCount += 1;
    saveEng();
  };

  // --- heartbeat back-off --------------------------------------------------
  let pingInterval = PING_START_MS;
  let pingTimer: number | null = null;

  const clearPing = (): void => {
    if (pingTimer !== null) {
      clearTimeout(pingTimer);
      pingTimer = null;
    }
  };

  const schedulePing = (): void => {
    clearPing();
    pingTimer = window.setTimeout(() => {
      pingTimer = null;
      checkpoint();
      if (pageVisit)
        enqueue("engagement_ping", pageVisit.id, {
          active_time: currentActiveTime(),
        });
      // Fixed 5s cadence: step is 0 and start == cap, so the gap never grows.
      pingInterval = Math.min(PING_MAX_MS, pingInterval + PING_STEP_MS);
      schedulePing();
    }, pingInterval);
  };

  const resetPing = (): void => {
    pingInterval = PING_START_MS;
    schedulePing();
  };

  // --- focus / blur transitions -------------------------------------------
  // `tab_blur`/`tab_focus` are debounced so rapid alt-tabbing (< ~600ms) does
  // not flood the buffer; the accumulator math stays exact regardless because
  // timing is folded immediately in pause()/resume().
  let pendingBlurTimer: number | null = null;

  const pause = (): void => {
    if (focusedSince === null) return; // already paused / loaded hidden
    checkpoint(); // fold the focused span before we stop counting
    focusedSince = null;
    blurAt = now();
    clearPing();
    flush(true); // durability: drain the buffer before the tab is backgrounded
    if (pendingBlurTimer !== null) clearTimeout(pendingBlurTimer);
    pendingBlurTimer = window.setTimeout(() => {
      pendingBlurTimer = null;
      if (pageVisit)
        enqueue("tab_blur", pageVisit.id, { active_time: currentActiveTime() });
    }, BLUR_DEBOUNCE_MS);
  };

  const resume = (): void => {
    if (focusedSince !== null) return; // already focused
    focusedSince = now();
    checkpoint();
    schedulePing(); // restart WITHOUT resetting the interval (§1.7)
    const away = now() - blurAt;
    if (pendingBlurTimer !== null && away < BLUR_DEBOUNCE_MS) {
      // Sub-second flip-flop: cancel the pending tab_blur, suppress tab_focus.
      clearTimeout(pendingBlurTimer);
      pendingBlurTimer = null;
      return;
    }
    if (pageVisit) enqueue("tab_focus", pageVisit.id, { away_ms: away });
  };

  // --- wiring --------------------------------------------------------------
  // On real unload: checkpoint dwell, emit the final page_leave, then a
  // best-effort session_end (lossy hint — the server recomputes the truth from
  // the event log, §1.7), and finally drain the buffer via beacon.
  addEventListener("pagehide", () => {
    try {
      checkpoint();
      emitPageLeave();
      emitFormAbandons();
      const s = getSession();
      if (pageVisit)
        enqueue("session_end", pageVisit.id, {
          duration: now() - s.started_at,
          page_count: pageCount,
          active_time: currentActiveTime(),
        });
    } catch {
      /* fail silent */
    }
    // Persist before the beacon: if sendBeacon is refused/unsent the batch is
    // already on disk to replay on the next load (backlog #5).
    persistBuffer();
    flush(true);
  });
  // Focus loss/gain drives the active_time accumulator + durability flush.
  addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") pause();
    else resume();
  });
  addEventListener("blur", () => pause());
  addEventListener("focus", () => resume());
  // Connectivity restored: drain any backlog that piled up while offline (#5).
  addEventListener("online", () => flush(false));

  try {
    loadEng();
    focusedSince = document.visibilityState === "visible" ? now() : null;

    // Cookieless privacy guard (backlog #7): an opted-out visitor (GPC/DNT) gets
    // an ephemeral random id cached only for this session, sent as a non-null
    // visitor_id so the collector never derives a stable fingerprint for them.
    if (privacyOptOut && readVisitorId() === null) writeVisitorId(uuid());

    // L3 replay (backlog #5): prepend any backlog persisted from a prior page
    // load (e.g. tab closed while offline) so it flushes on this load. No-op for
    // opted-out sessions — loadPersisted returns [] when privacyOptOut.
    requeue(loadPersisted());
    if (buffer.length > 0) scheduleFlush();

    // Person capture (§1.4): restore any persisted identity, scan the URL for
    // an email param, and expose the async command-queue host API.
    loadIdentity();
    captureUrlEmail();
    installHostApi();

    patchHistory("pushState");
    patchHistory("replaceState");
    addEventListener("popstate", () => handleRouteChange());
    addEventListener("hashchange", () => handleRouteChange());
    addEventListener("click", onClick, true);
    addEventListener("scroll", onScroll, { passive: true });
    // Form lifecycle (delegated, capture phase — mirrors onClick).
    addEventListener("focusin", onFormStart, true);
    addEventListener("submit", onSubmit, true);
    addEventListener("click", onSubmitClick, true);
    addEventListener("keydown", onSubmitKey, true);

    startPageVisit();
    if (sessionIsNew) {
      refreshVisitorId(); // slide the cookie window once per session
      enqueue("session_start", pageVisit.id, {
        landing_page: currentUrl(),
        referrer: document.referrer,
      });
    }
    trackPageView();
  } catch {
    /* fail silent — never break the host page */
  }
})();
